import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import CustomButton from 'lib/designSystem/Button';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';
import { useState } from 'react';

const ADJUSTABLE_FIELD_CONFIG: Record<
  AdjustableRateField,
  {
    label: string;
    min: number;
    max: number;
    step: number;
    suffix: string;
  }
> = {
  variableDiscount: {
    label: 'Descuento Variable',
    min: 0,
    max: 100,
    step: 0.01,
    suffix: '%',
  },
  fixedDiscount: {
    label: 'Descuento Fijo',
    min: 0,
    max: 100,
    step: 0.01,
    suffix: '%',
  },
  variableRate: {
    label: 'Tasa Variable',
    min: 0.01,
    max: 200,
    step: 0.01,
    suffix: '%',
  },
  fixedRate: {
    label: 'Tasa Fija',
    min: 0.01,
    max: 500,
    step: 0.01,
    suffix: 'UF',
  },
};

export type AdjustableRateField =
  | 'variableDiscount'
  | 'fixedDiscount'
  | 'variableRate'
  | 'fixedRate';

type Message = { severity: 'success' | 'info' | 'error'; text: string };

interface ReverseCalculationPanelProps {
  show: boolean;
  rateControls: RiskPremiumRates;
  setRateControls: (value: RiskPremiumRates) => void;
  amount: number;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  quotedCurrency: string;
}

const ReverseCalculationPanel: React.FC<ReverseCalculationPanelProps> = ({
  show,
  rateControls,
  setRateControls,
  amount,
  startDate,
  endDate,
  quotedCurrency,
}) => {
  if (!show) return null;
  const { calculate } = riskPremiumCalculator();
  const [message, setMessage] = useState<Message | null>(null);
  const [targetEmissionCost, setTargetEmissionCost] = useState('');
  const [adjustableField, setAdjustableField] =
    useState<AdjustableRateField>('variableDiscount');

  const options = Object.keys(ADJUSTABLE_FIELD_CONFIG) as AdjustableRateField[];

  const handleTargetEmissionChange = (value: string) => {
    setTargetEmissionCost(value);
    setMessage(null);
  };

  const handleReverseCalculation = () => {
    const targetCost = Number(targetEmissionCost);

    if (!targetEmissionCost || Number.isNaN(targetCost) || targetCost < 0) {
      setMessage({
        severity: 'error',
        text: 'Ingrese un costo de emisión válido en CLP.',
      });
      return;
    }

    if (!amount || !startDate || !endDate) {
      setMessage({
        severity: 'error',
        text: 'Complete monto y fechas antes de calcular.',
      });
      return;
    }

    if (
      adjustableField !== 'variableRate' &&
      (rateControls.variableRate == null || rateControls.variableRate <= 0)
    ) {
      setMessage({
        severity: 'error',
        text: 'Debe definir una tasa variable válida.',
      });
      return;
    }

    if (
      adjustableField !== 'fixedRate' &&
      (rateControls.fixedRate == null || rateControls.fixedRate <= 0)
    ) {
      setMessage({
        severity: 'error',
        text: 'Debe definir una tasa fija válida.',
      });
      return;
    }

    try {
      const fieldConfig = ADJUSTABLE_FIELD_CONFIG[adjustableField];
      let bestValue = fieldConfig.min;
      let bestAmount = 0;
      let bestDifference = Number.POSITIVE_INFINITY;
      const coarseStep = fieldConfig.step;
      const fineStep = 0.0001;

      const evaluateValue = (testedValue: number) => {
        let result: Output | null = null;
        try {
          result = calculate({
            quotedAmount: amount,
            quotedCurrency,
            startDate,
            endDate,
            rates: {
              ...rateControls,
              [adjustableField]: testedValue,
            },
          });
        } catch (loopError) {
          return;
        }

        const difference = Math.abs(result.amount - targetCost);
        if (difference < bestDifference) {
          bestDifference = difference;
          bestValue = testedValue;
          bestAmount = result.amount;
        }
      };

      const totalCoarseSteps = Math.round(
        (fieldConfig.max - fieldConfig.min) / coarseStep,
      );

      for (let i = 0; i <= totalCoarseSteps; i += 1) {
        const testedValue = Number(
          (fieldConfig.min + i * coarseStep).toFixed(4),
        );
        evaluateValue(testedValue);
      }

      const fineMin = Math.max(fieldConfig.min, bestValue - coarseStep);
      const fineMax = Math.min(fieldConfig.max, bestValue + coarseStep);
      const totalFineSteps = Math.round((fineMax - fineMin) / fineStep);

      for (let i = 0; i <= totalFineSteps; i += 1) {
        const testedValue = Number((fineMin + i * fineStep).toFixed(4));
        evaluateValue(testedValue);
      }

      if (bestDifference === Number.POSITIVE_INFINITY) {
        setMessage({
          severity: 'error',
          text: `No se encontró un valor válido para ${fieldConfig.label.toLowerCase()}.`,
        });
        return;
      }

      setRateControls({
        ...rateControls,
        [adjustableField]: Number(bestValue.toFixed(4)),
      });

      if (bestDifference === 0) {
        setMessage({
          severity: 'success',
          text: `${fieldConfig.label} ajustado a ${bestValue.toFixed(4)}${fieldConfig.suffix} para alcanzar ${formatMoney(bestAmount)} CLP.`,
        });
      } else {
        setMessage({
          severity: 'info',
          text: `No existe un match exacto. Se aplicó ${fieldConfig.label.toLowerCase()} en ${bestValue.toFixed(4)}${fieldConfig.suffix} y el costo resultante es ${formatMoney(bestAmount)} CLP.`,
        });
      }
    } catch (error) {
      console.error(error);
      setMessage({
        severity: 'error',
        text: 'No fue posible realizar el cálculo inverso.',
      });
    }
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}
      >
        Cálculo Inverso por Costo Objetivo
      </Typography>
      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Parámetro a ajustar"
            value={adjustableField}
            onChange={(e) =>
              setAdjustableField(e.target.value as AdjustableRateField)
            }
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {ADJUSTABLE_FIELD_CONFIG[option as AdjustableRateField].label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            size="small"
            label=""
            type="number"
            value={targetEmissionCost}
            onChange={(e) => handleTargetEmissionChange(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">CLP</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomButton
            color="primary"
            onClick={handleReverseCalculation}
            fullWidth
            style={{ height: '40px' }}
          >
            Calcular
          </CustomButton>
        </Grid>
      </Grid>
      {message && (
        <Alert severity={message.severity} sx={{ mt: 1.5, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {message.text}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ReverseCalculationPanel;
